import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Normalize timestamp to milliseconds (handles both seconds and milliseconds).
 * API returns Unix seconds (10-digit), JavaScript needs milliseconds (13-digit).
 *
 * @example
 * normalizeTimestamp(1704067200)    // Returns 1704067200000 (converted from seconds)
 * normalizeTimestamp(1704067200000) // Returns 1704067200000 (already in ms)
 */
export function normalizeTimestamp(timestamp: number): number {
  // If timestamp is less than year 2000 in ms, it's likely in seconds
  return timestamp < 946684800000 ? timestamp * 1000 : timestamp
}

/**
 * Get current time in Unix seconds.
 * Use this instead of `Math.floor(Date.now() / 1000)`.
 *
 * @example
 * const now = nowSeconds()
 * if (dueDate < now) { // overdue }
 */
export function nowSeconds(): number {
  return Math.floor(Date.now() / 1000)
}

/**
 * Convert Unix seconds timestamp to JavaScript Date object.
 * Use this instead of `new Date(timestamp * 1000)`.
 *
 * @example
 * const date = fromUnixSeconds(event.unix_start)
 * return date.toLocaleDateString()
 */
export function fromUnixSeconds(timestamp: number): Date {
  return new Date(normalizeTimestamp(timestamp))
}

/**
 * Convert JavaScript Date to Unix seconds timestamp.
 *
 * @example
 * const unix = toUnixSeconds(new Date())
 */
export function toUnixSeconds(date: Date): number {
  return Math.floor(date.getTime() / 1000)
}

/**
 * Get start of day (00:00:00) in Unix seconds for a given date.
 * Defaults to today if no date provided.
 *
 * @example
 * const startOfDay = startOfDaySeconds()
 * filter.unix_start = { $gte: startOfDay }
 */
export function startOfDaySeconds(date?: Date): number {
  const d = date ? new Date(date) : new Date()
  d.setHours(0, 0, 0, 0)
  return Math.floor(d.getTime() / 1000)
}

/**
 * Get end of day (23:59:59) in Unix seconds for a given date.
 * Defaults to today if no date provided.
 *
 * @example
 * const endOfDay = endOfDaySeconds()
 * filter.unix_start = { $lt: endOfDay }
 */
export function endOfDaySeconds(date?: Date): number {
  const d = date ? new Date(date) : new Date()
  d.setHours(23, 59, 59, 999)
  return Math.floor(d.getTime() / 1000)
}

/**
 * Check if a Unix seconds timestamp is overdue (before current time).
 *
 * @example
 * if (isOverdue(task.due_date)) {
 *   return <Badge variant="destructive">Overdue</Badge>
 * }
 */
export function isOverdue(timestamp: number): boolean {
  if (!timestamp || timestamp === 0) return false
  return timestamp < nowSeconds()
}

/**
 * Check if a Unix seconds timestamp falls within today.
 *
 * @example
 * if (isToday(event.unix_start)) {
 *   return <Badge>Today</Badge>
 * }
 */
export function isToday(timestamp: number): boolean {
  if (!timestamp) return false
  const start = startOfDaySeconds()
  const end = endOfDaySeconds()
  return timestamp >= start && timestamp <= end
}

/**
 * Format a timestamp as a human-readable date
 */
export function formatDate(timestamp: number): string {
  const date = new Date(normalizeTimestamp(timestamp))
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

/**
 * Format a timestamp as a relative time string (e.g., "5 minutes ago")
 */
export function formatRelativeTime(timestamp: number): string {
  const normalizedTs = normalizeTimestamp(timestamp)
  const now = Date.now()
  const diff = now - normalizedTs
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (seconds < 60) return "just now"
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days === 1) return "yesterday"
  if (days < 7) return `${days}d ago`

  return formatDate(timestamp)
}

/**
 * Format a duration in seconds as a human-readable string (e.g., "2:34")
 */
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

/**
 * Get initials from a name (e.g., "John Doe" -> "JD")
 */
export function getInitials(name: string | undefined | null): string {
  if (!name) return ""
  return name
    .split(" ")
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Format a phone number for display (e.g., "(555) 123-4567")
 */
export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, "")
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

/**
 * Validate and adjust a date range to ensure it doesn't exceed the maximum allowed days.
 * Used by supervisor pages that have a 32-day max range requirement.
 *
 * @param startDate - Start of the date range
 * @param endDate - End of the date range
 * @param maxDays - Maximum number of days allowed (default 32)
 * @returns Adjusted date range that respects the max days limit
 *
 * @example
 * const { start, end } = validateDateRange(startDate, endDate, 32)
 * // If range exceeds 32 days, end is adjusted to 32 days after start
 */
export function validateDateRange(
  startDate: Date | null,
  endDate: Date | null,
  maxDays: number = 32
): { start: Date | null; end: Date | null } {
  if (startDate && endDate) {
    const diffDays = Math.floor(
      (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
    )
    if (diffDays > maxDays) {
      // Adjust end date to maxDays after start
      const newEnd = new Date(startDate)
      newEnd.setDate(newEnd.getDate() + maxDays)
      return { start: startDate, end: newEnd }
    }
  }
  return { start: startDate, end: endDate }
}

/**
 * Handle start date change with max range validation.
 * Adjusts end date if the range would exceed maxDays.
 */
export function handleStartDateWithRange(
  newStart: Date | null,
  currentEnd: Date | null,
  maxDays: number = 32
): { start: Date | null; end: Date | null } {
  if (!newStart) return { start: null, end: currentEnd }

  if (currentEnd) {
    const endUnix = Math.floor(currentEnd.getTime() / 1000)
    const startUnix = Math.floor(newStart.getTime() / 1000)
    if (endUnix > startUnix + 86400 * maxDays) {
      // Adjust end to end of month from start
      const newEnd = new Date(newStart)
      newEnd.setMonth(newEnd.getMonth() + 1)
      newEnd.setDate(0) // Last day of start month
      newEnd.setHours(23, 59, 59, 999)
      return { start: newStart, end: newEnd }
    }
  }
  return { start: newStart, end: currentEnd }
}

/**
 * Handle end date change with max range validation.
 * Adjusts start date if the range would exceed maxDays.
 */
export function handleEndDateWithRange(
  newEnd: Date | null,
  currentStart: Date | null,
  maxDays: number = 32
): { start: Date | null; end: Date | null } {
  if (!newEnd) return { start: currentStart, end: null }

  if (currentStart) {
    const startUnix = Math.floor(currentStart.getTime() / 1000)
    const endUnix = Math.floor(newEnd.getTime() / 1000)
    if (endUnix > startUnix + 86400 * maxDays) {
      // Adjust start to beginning of end month
      const newStart = new Date(newEnd)
      newStart.setDate(1)
      newStart.setHours(0, 0, 0, 0)
      return { start: newStart, end: newEnd }
    }
  }
  return { start: currentStart, end: newEnd }
}

/**
 * Throttle function that runs at the end of a delay period.
 * If callTimes is set, runs immediately after that many calls OR after the delay.
 *
 * @param fn - Function to throttle
 * @param delay - Delay in milliseconds (default 250)
 * @param callTimes - Number of calls before running immediately (0 = always wait for delay)
 */
export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  delay: number = 250,
  callTimes: number = 0
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  let calls = 0

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout)
    }
    calls++

    if (callTimes > 0 && calls > callTimes) {
      fn(...args)
      calls = 0
    } else {
      timeout = setTimeout(() => {
        fn(...args)
        calls = 0
      }, delay)
    }
  }
}
